import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Message } from '../../types/message';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface MessageNotificationProps {
  message: Message;
  onClose: () => void;
  autoCloseDuration?: number;
}

const MessageNotification: React.FC<MessageNotificationProps> = ({
  message,
  onClose,
  autoCloseDuration = 5000,
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [textVisible, setTextVisible] = useState(true);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);

    // Hide text after timeout
    const textTimer = setTimeout(() => {
      setTextVisible(false);
    }, autoCloseDuration);

    return () => clearTimeout(textTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCloseDuration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  const handleClick = () => {
    navigate('/messages');
    handleClose();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (!textVisible) {
    // Show only avatar after text disappears
    return (
      <div
        onClick={handleClick}
        className="fixed bottom-4 left-4 z-50 transition-all duration-300 cursor-pointer hover:scale-110"
      >
        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold shadow-lg">
          {getInitials(message.senderName)}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed bottom-4 left-4 z-50 transition-all duration-300 ${
        isVisible && !isLeaving
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0'
      }`}
    >
      <div
        onClick={handleClick}
        className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm cursor-pointer hover:shadow-2xl transition-shadow"
      >
        <div className="flex items-start space-x-3">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold">
              {getInitials(message.senderName)}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">{message.senderName}</p>
            <p className="text-sm text-gray-600 truncate mt-0.5">{message.content}</p>
          </div>

          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageNotification;
